import { Fragment, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import AdjustmentList from './AdjustmentList';
import { fmtMoney } from './fmtMoney';
import LineStatusPill from './LineStatusPill';
import SplitLineDetails from './SplitLineDetails';

const TABLE_COLUMN_COUNT = 9;

/**
 * Match a canonical (flat) appealable line to its normalized split group.
 * Primary key is lineNumber + procedureCode; falls back to lineNumber alone.
 * The flat line stays the unit of selection — the group only adds provenance.
 */
function buildGroupLookup(groups) {
  const byKey = new Map();
  const byLineNumber = new Map();
  (groups || []).forEach((g) => {
    byKey.set(`${g.lineNumber}|${g.procedureCode}`, g);
    if (!byLineNumber.has(String(g.lineNumber))) byLineNumber.set(String(g.lineNumber), g);
  });
  return (line) =>
    byKey.get(`${line.line_number}|${line.procedure_code}`)
    || byLineNumber.get(String(line.line_number))
    || null;
}

function paidColor(paid, billed) {
  const p = parseFloat(paid || 0);
  const b = parseFloat(billed || 0);
  if (p === 0) return '#C62828';
  if (p < b) return '#E65100';
  return '#2E7D32';
}

function zeroPad2(n) {
  return String(n || 0).padStart(2, '0');
}

/**
 * Enriched line-items table with:
 * - Indeterminate header checkbox toggling all unlocked lines
 * - Select All Appealable / Clear Selection buttons
 * - Adjustments column (denial codes via AdjustmentList)
 * - Color-coded Paid column
 */
export default function LineItemsTable({
  lines = [],
  selectedLines,
  onLineToggle,
  disabled,
  normalizedGroups = [],
}) {
  const [expandedLines, setExpandedLines] = useState({});
  const findGroup = buildGroupLookup(normalizedGroups);
  const toggleExpanded = (lineId) =>
    setExpandedLines((prev) => ({ ...prev, [lineId]: !prev[lineId] }));

  const appealableLines = lines.filter((ln) => !ln.appeal_locked);
  const selectedAppealableCount = appealableLines.filter((ln) => !!selectedLines[ln.id]).length;
  const allSelected = appealableLines.length > 0 && selectedAppealableCount === appealableLines.length;
  const someSelected = selectedAppealableCount > 0 && !allSelected;

  const handleSelectAll = () => {
    appealableLines.forEach((ln) => {
      if (!selectedLines[ln.id]) onLineToggle(ln);
    });
  };

  const handleClearAll = () => {
    appealableLines.forEach((ln) => {
      if (selectedLines[ln.id]) onLineToggle(ln);
    });
  };

  const handleHeaderToggle = () => {
    if (allSelected) {
      handleClearAll();
    } else {
      handleSelectAll();
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={handleSelectAll}
          disabled={disabled || allSelected || appealableLines.length === 0}
          sx={{ fontSize: '0.72rem', py: 0.25 }}
        >
          Select All Appealable
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={handleClearAll}
          disabled={disabled || selectedAppealableCount === 0}
          sx={{ fontSize: '0.72rem', py: 0.25 }}
        >
          Clear Selection
        </Button>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.75rem' } }}>
            <TableCell padding="checkbox">
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={handleHeaderToggle}
                disabled={disabled || appealableLines.length === 0}
                inputProps={{ 'aria-label': 'select all appealable lines' }}
              />
            </TableCell>
            <TableCell>Line #</TableCell>
            <TableCell>CPT + Mod</TableCell>
            <TableCell>Date</TableCell>
            <TableCell align="right">Units</TableCell>
            <TableCell align="right">Charged</TableCell>
            <TableCell align="right">Paid</TableCell>
            <TableCell>Adjustments</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lines.map((ln) => {
            const isSelected = !!selectedLines[ln.id];
            const isLocked = !!ln.appeal_locked;
            const rowDisabled = disabled || isLocked;
            const group = findGroup(ln);
            const isSplit = !!group?.splitDetected;
            const isExpanded = !!expandedLines[ln.id];
            return (
              <Fragment key={ln.id}>
              <TableRow
                hover
                selected={isSelected}
                onClick={() => !rowDisabled && onLineToggle(ln)}
                sx={{
                  cursor: rowDisabled ? 'default' : 'pointer',
                  opacity: isLocked ? 0.65 : 1,
                  '& > td': { borderBottom: isExpanded ? 'none' : undefined },
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={isSelected}
                    disabled={rowDisabled}
                    onChange={() => onLineToggle(ln)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {zeroPad2(ln.line_number)}
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {ln.procedure_code}
                  {ln.modifiers && ln.modifiers.length > 0 && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                      {ln.modifiers.join(' ')}
                    </Typography>
                  )}
                  {isSplit && (
                    <Chip
                      label={`Split · ${group.sourceLineCount}`}
                      size="small"
                      variant="outlined"
                      color="warning"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpanded(ln.id);
                      }}
                      title="Optum split this submitted line into multiple service lines — click to view the breakdown"
                      sx={{ ml: 0.75, height: 18, fontSize: '0.62rem', cursor: 'pointer' }}
                    />
                  )}
                </TableCell>
                <TableCell sx={{ fontSize: '0.8rem' }}>{ln.service_start_date || '—'}</TableCell>
                <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{ln.unit_count ?? '—'}</TableCell>
                <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{fmtMoney(ln.billed_amount)}</TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: paidColor(ln.paid_amount, ln.billed_amount),
                  }}
                >
                  {fmtMoney(ln.paid_amount)}
                </TableCell>
                <TableCell sx={{ maxWidth: 260 }}>
                  <AdjustmentList
                    denialCodes={ln.denial_codes}
                    billedAmount={ln.billed_amount}
                    paidAmount={ln.paid_amount}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                    <LineStatusPill status={ln.status} />
                    {isLocked && (
                      <Typography variant="caption" color="text.secondary">
                        Already on {ln.existing_ticket_number || 'another ticket'}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
              {isSplit && (
                <TableRow>
                  <TableCell colSpan={TABLE_COLUMN_COUNT} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ py: 1 }}>
                        <SplitLineDetails group={group} />
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}
